import React, { FC, useRef, useState, useEffect } from 'react'
import { ItemProps, FilePreview } from '../../types'
import Splash from '@/components/splash/Splash'
import ValidationError from '../ValidationError'
import fetchFileFromUrl from '../../utils/file/fetchFileFromUrl'
import createFileData from '@/components/Form/utils/file/createFileData'
import { v4 as uuidv4 } from 'uuid'

const File: FC<ItemProps> = (
	{
		formItem,
		validation,
		disabled = false,
		formData,
		onChange,
		onFocus,
		onBlur,
		onKeyDown,
		autoFocus,
		//updateForm
	},
) => {


	const inputFileRef = useRef<HTMLInputElement | null>(null)
	const [fileInfo, setFileInfo] = useState<FilePreview | null>(null)
	const [loading, setLoading] = useState<boolean>(false)
	const [isDragging, setIsDragging] = useState<boolean>(false)



	const handleImageChange = async (file: File | null, url: string | null = null, fileExtension: string | null = null) => {

		const filePreview = await createFileData({
			file,
			fileExtension,
			url,
			types: formItem.itemOptions?.acceptFormat,
		})

		if (filePreview) {
			setFileInfo(filePreview)
			onChange?.(formItem.name, {
				...filePreview,
				loaded: true,
			})
		}
	}



	const fetchFileFromDefaultValue = async (url: string) => {
		setLoading(true)
		try {
			const file: any = await fetchFileFromUrl(url)
			if (!file?.file) {
				// Failed to fetch file, but still allow editing with URL only
				const fallbackPreview: FilePreview = {
					uuid: uuidv4(),
					name: url.split('/').pop() || 'file',
					size: 0,
					type: '',
					string: url,
					url: url,
					data: null,
				}
				setFileInfo(fallbackPreview)
				onChange?.(formItem.name, {
					...fallbackPreview,
					loaded: true,
				})
				return
			}
			const filePreview = await createFileData({
				file: file.file,
				fileExtension: file.fileExtension,
				url: url,
				types: formItem.itemOptions?.acceptFormat,
			})
			if (filePreview) {
				setFileInfo(filePreview)
				onChange?.(formItem.name, {
					...filePreview,
					loaded: true,
				})
			}
		} catch (error) {
			console.error('Error fetching file from URL:', error)
			// Allow editing even if file fetch fails
			const fallbackPreview: FilePreview = {
				uuid: uuidv4(),
				name: url.split('/').pop() || 'file',
				size: 0,
				type: '',
				string: url,
				url: url,
				data: null,
			}
			setFileInfo(fallbackPreview)
			onChange?.(formItem.name, {
				...fallbackPreview,
				loaded: true,
			})
		} finally {
			setLoading(false)
		}
	}



	useEffect(() => {

		if (
			typeof formData[formItem.name] === 'string' &&
			formData[formItem.name].startsWith('http') &&
			(!fileInfo || formData[formItem.name] !== fileInfo)
		) {
			fetchFileFromDefaultValue(formData[formItem.name])
		}

		if (formData[formItem.name] !== fileInfo) setFileInfo(formData[formItem.name])



	}, [formData[formItem.name], fileInfo, formData])



	const handleClick = () => {
		if (!loading) inputFileRef.current?.click()
	}



	const handleDragOver = (e: React.DragEvent) => {
		e.preventDefault()
		setIsDragging(true)
	}



	const handleDragLeave = () => {
		setIsDragging(false)
	}



	const handleDrop = (e: React.DragEvent) => {
		e.preventDefault()
		setIsDragging(false)

		const file = e.dataTransfer.files[0]
		if (file && e.dataTransfer.files.length === 1) {
			handleImageChange(file)
		} else {
			alert('Please drop only one file.')
		}
	}



	return (
		<>
			<div
				className={`relative group h-[96px] select-none ${isDragging ? 'border-primary-500 bg-gray-100' : ''} ${formItem.classNames?.content || ''}`}
				onDragOver={handleDragOver}
				onDragLeave={handleDragLeave}
				onDrop={handleDrop}
			>
				<div
					className={`transition-all h-full outline-none overflow-hidden ${disabled ? 'disabled' : ''} ` +
						`flex flex-col gap-xs  w-full bg-white rounded-md shadow border border-gray-300 ` +
						`justify-center items-center hover:border-primary-500 cursor-pointer active:shadow-focus ` +
						`${fileInfo ? '' : 'border-dashed'} ` +
						`${isDragging ? `border-primary-500 ${(validation && !loading) ? 'shadow-red' : 'shadow-focus'}` : ''} ` +
						`${(validation && !loading) ? '!border-red-500 border-2 focus:border-red-600 active:shadow-red hover:border-red-600 !border-solid' : ''}`
					}
					onClick={handleClick}
				>
					<Splash className={'!bg-opacity-50 !rounded-md m-[1px] '} isOpen={loading} />
					{!fileInfo ? (
						<>
							<div className="text-center text-gray-400 text-title-lg">
								{formItem.placeholder || 'Drag and drop your files here'}
							</div>
							<div className="text-center text-gray-400 text-text-sm">
								{formItem.itemOptions?.hint || 'or click to select files from your device.'}
							</div>
						</>
					) : (
						<>
							<div
								className={
									'w-full h-full p-md bg-gray-100 flex items-center gap-md relative ' +
									`${isDragging ? 'border-primary-500 !bg-white shadow-focus ' : ''}`
								}
							>
								{fileInfo.url &&
									<div
										onMouseDown={(e) => e.preventDefault()}
										onClick={(e) => {
											e.stopPropagation()
											const link = document.createElement('a')
											link.href = fileInfo.url || ''
											link.download = '' // keep empty → browser downloads
											link.click()
										}}
										className={'bg-transparent hover:bg-primary-500 hover:text-white active:bg-primary-600 active:text-white text-primary-700 text-sm font-icon-regular absolute top-xs right-[45px] w-[35px] h-[35px] transition-all rounded-md flex items-center justify-center'}
									>
										
									</div>
								}
								<div
									onMouseDown={(e) => e.preventDefault()}
									onClick={(e) => {
										e.stopPropagation()
										setFileInfo(null)
										onChange?.(formItem.name, null)
										if (inputFileRef.current) {
											inputFileRef.current.value = ''
										}
									}}
									className={'bg-transparent hover:bg-red-500 hover:text-white active:bg-red-600 active:text-white text-red-500 text-sm font-icon-regular absolute top-xs right-xs w-[35px] h-[35px] transition-all rounded-md flex items-center justify-center'}
								>
									
								</div>
								{formItem.itemOptions?.filePreview && (
									<div>{formItem.itemOptions.filePreview(fileInfo)}</div>
								)}
								{!loading &&
									<div className="grow flex flex-col justify-center items-start gap-1">
										<div className="text-gray-900 text-title-sm">{truncateFileName(fileInfo?.name)}</div>
										<div className="text-gray-400 text-text-sm">{formatFileSize(fileInfo.size)}</div>
									</div>
								}
								{loading &&
									<div className="grow flex flex-col justify-center items-start gap-1">
										<div className="text-gray-900 text-title-sm">Loading ...</div>
										<div className="text-gray-400 text-text-sm">Please wait</div>
									</div>
								}

							</div>
						</>
					)}
				</div>
				<input
					ref={inputFileRef}
					type="file"
					accept={formItem.itemOptions?.acceptFormat || 'image/*'}
					className="hidden"
					disabled={disabled}
					onChange={(e) => {
						handleImageChange(e.target.files?.[0] || null)
						// Reset input value so selecting the same file again triggers onChange
						if (inputFileRef.current) inputFileRef.current.value = ''
					}}
					onFocus={onFocus}
					onBlur={onBlur}
					onKeyDown={onKeyDown}
					autoFocus={autoFocus}
				/>
			</div>
			{!loading &&
				<ValidationError error={validation} formItem={formItem} />
			}

		</>
	)
}

function truncateFileName(name?: string): string {
	if (!name) return ''
	const lastDot = name.lastIndexOf('.')
	const ext = lastDot !== -1 ? name.slice(lastDot) : ''
	const base = lastDot !== -1 ? name.slice(0, lastDot) : name
	const maxBase = 12
	if (base.length <= maxBase) return name
	return base.slice(0, 6) + '...' + base.slice(-4) + ext
}

// Utility function to calculate and format file size
function formatFileSize(size: number) {
	const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB']
	if (size === 0) return ''
	const i = Math.floor(Math.log(size) / Math.log(1024))
	return (size / Math.pow(1024, i)).toFixed(2) + ' ' + sizes[i]
}

export default File
